import { useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

import { api } from "../../convex/_generated/api";
import { PaneHeader } from "@/components/settings/frame";
import { Button } from "@/components/ui/button";
import { authClient } from "@/lib/auth-client";

// Deleting the account, on a page of its own and in the same plain type as
// the others: what it does, said from the account (shared or alone,
// subscribed or not), and one button. Where emails are sent it takes two
// steps: the button emails a link, and the link comes back here
// (?deleteAccount=…) for the last word. Elsewhere it is one step, for a
// session less than a day old (convex/account.ts, convex/auth.ts).
export const Route = createFileRoute("/settings/delete")({
  validateSearch: (search: Record<string, unknown>): { deleteAccount?: string } => ({
    deleteAccount: typeof search.deleteAccount === "string" ? search.deleteAccount : undefined,
  }),
  component: DeletePage,
});

function DeletePage() {
  const { t } = useTranslation("settings");
  const navigate = useNavigate();
  const me = useQuery(api.auth.getCurrentUser);
  const providers = useQuery(api.auth.getAuthProviders);
  const preview = useQuery(api.account.preview);
  // The emailed link's token, taken out of the address bar at once.
  const search = Route.useSearch();
  const [token] = useState(() => search.deleteAccount ?? null);
  useEffect(() => {
    if (search.deleteAccount) void navigate({ to: "/settings/delete", search: {}, replace: true });
  }, [search.deleteAccount, navigate]);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  // Before the link: ask for it. With it, or without emails: delete.
  const asksForLink = token === null && Boolean(providers?.email);
  const ready = preview !== undefined && providers !== undefined && me !== undefined;

  async function onConfirm() {
    setBusy(true);
    try {
      const res = await authClient.deleteUser(token ? { token } : {});
      if (res.error) {
        const code = res.error.code;
        if (code === "SESSION_EXPIRED") toast.error(t("account.delete.stale"));
        else if (token && (code === "INVALID_TOKEN" || res.error.status === 404)) {
          toast.error(t("account.delete.invalid"));
        } else toast.error(t("account.delete.failed"));
        return;
      }
      if (res.data?.message === "Verification email sent") {
        setSent(true);
        return;
      }
      window.location.assign("/login");
    } catch {
      toast.error(t("account.delete.failed"));
    } finally {
      setBusy(false);
    }
  }

  const facts = [
    preview?.shared ? t("account.delete.shared") : t("account.delete.alone"),
    preview?.subscribed ? t("account.delete.subscribed") : null,
    asksForLink ? t("account.delete.emailStep") : t("account.delete.permanent"),
  ].filter((fact): fact is string => fact !== null);

  return (
    <div>
      <PaneHeader title={t("nav.delete")} description={t("remove.lede")} />
      {ready ? (
        <ul className="max-w-[35rem] border-t border-line">
          {facts.map((fact) => (
            <li key={fact} className="border-b border-line py-3 text-sm leading-relaxed text-ink-2">
              {fact}
            </li>
          ))}
        </ul>
      ) : (
        <div className="h-36 max-w-[35rem] animate-pulse rounded-md bg-paper-2" />
      )}
      <div className="mt-6">
        {sent ? (
          <p role="status" className="text-sm text-ink-2">
            {t("account.delete.sent", { email: me?.email ?? "" })}
          </p>
        ) : (
          <Button
            variant="outline"
            size="sm"
            className="border-destructive/30 text-destructive hover:bg-destructive/5 hover:text-destructive"
            disabled={busy || !ready}
            onClick={onConfirm}
          >
            {busy
              ? t("account.delete.deleting")
              : asksForLink
                ? t("account.delete.sendLink")
                : t("account.delete.confirm")}
          </Button>
        )}
      </div>
    </div>
  );
}
